import React, { useEffect, useState } from "react";
import { api, errorMessage } from "../api/client";
import type { Part, TryResult } from "../api/types";
import { Card } from "../components/Controls";
import { VerdictStamp } from "../components/VerdictStamp";
import { Dropzone } from "../components/Dropzone";
import { fmtNum } from "../lib/format";

export function TryPhotoCard({ part }: { part: Part }) {
  const [preview, setPreview] = useState<string | null>(null);
  const [result, setResult] = useState<TryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview]
  );

  const run = async (files: File[]) => {
    const f = files[0];
    if (!f || busy) return;
    setPreview(URL.createObjectURL(f));
    setResult(null);
    setError(null);
    setBusy(true);
    const fd = new FormData();
    fd.append("image", f);
    try {
      setResult(await api.form<TryResult>("POST", `/parts/${part.id}/try`, fd));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const ng = result?.result === "NG";
  return (
    <Card className="p-5" data-testid="try-photo-card">
      <h3 className="font-display text-base font-semibold">Probar con una foto</h3>
      <p className="mb-3 mt-1 text-xs text-slate-500">
        Subir una foto de la pieza: el modelo activo indica si está bien o mal y resalta la zona de la falla. No se guarda nada: ni la foto ni una inspección.
      </p>
      <div className="grid gap-4 md:grid-cols-[1fr,1.4fr]">
        <Dropzone onFiles={run} label={busy ? "Analizando…" : "Arrastrar una foto o tocar para elegirla"} testId="try-photo-dropzone" />
        <div>
          {error && <p className="text-sm text-ng" data-testid="try-photo-error">{error}</p>}
          {result && (
            <div className="space-y-2" data-testid="try-photo-result" data-result={result.result}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <VerdictStamp result={result.result} size="md" testId="try-photo-badge" />
                <span className="text-sm font-medium">{ng ? "Se sale de lo normal" : "Dentro de lo normal"}</span>
                <span className="font-mono text-xs text-slate-500">
                  puntaje {fmtNum(result.score)} · umbral {fmtNum(result.threshold)} · {fmtNum(result.infer_ms, 0)} ms
                </span>
              </div>
              <div className="reticle overflow-hidden rounded-lg bg-graphite">
                <img
                  src={result.heatmap_data_url || preview || ""}
                  alt={ng ? "Zona de la falla resaltada" : "Foto probada"}
                  className="max-h-72 w-full object-contain"
                  data-testid="try-photo-image"
                />
              </div>
              {ng && <p className="text-xs text-slate-500">En rojo, las zonas que más se alejan de las fotos OK con las que se entrenó.</p>}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
